import { useEffect, useState } from "react";
import { useAuth } from "@repo/auth";
import { useTranslation } from "@repo/i18n";
import { css } from "@repo/styled-system/css";
import { useCurrentTrial, type TrialBanner as Trial } from "./trialApi";

/**
 * The account-root TRIAL countdown banner (sales-trials.md "Countdown visibility", R1). The account root
 * sees — from within the product — the trial's remaining time (a LIVE per-second countdown), the included
 * entitlements, and a convert CTA, so expiry is never a surprise. Before activation it shows the pending
 * state + the 1-business-day activation commitment (open question 3). READ-ONLY: no start/extend/end here
 * (those are operator-only on `/ops`, R1). Rendered only for the account root; a manager/waiter sees nothing.
 */
export function TrialBanner() {
  const { claims } = useAuth();
  const { t } = useTranslation();
  const isAccountRoot = claims?.isAccountRoot === true;
  const trialQuery = useCurrentTrial(isAccountRoot);
  const trial = trialQuery.data ?? null;

  if (!isAccountRoot || !trial) return null;
  if (trial.state !== "REQUESTED" && trial.state !== "ACTIVE") return null;

  return trial.state === "REQUESTED" ? (
    <PendingBanner trial={trial} t={t} />
  ) : (
    <ActiveBanner trial={trial} t={t} />
  );
}

type Translate = (key: string, opts?: Record<string, unknown>) => string;

function PendingBanner({ trial, t }: { trial: Trial; t: Translate }) {
  const due = new Date(trial.activationDueAtSec * 1000).toLocaleString();
  return (
    <output
      data-testid="trial-banner-pending"
      className={css({
        display: "block",
        p: "3",
        mb: "3",
        rounded: "md",
        borderWidth: "1px",
        borderColor: "border.default",
        bg: "bg.subtle",
      })}
    >
      <strong>{t("trial.pending.title")}</strong>
      <p className={css({ color: "fg.muted", fontSize: "sm" })}>
        {t("trial.pending.commitment", { due })}
      </p>
    </output>
  );
}

function ActiveBanner({ trial, t }: { trial: Trial; t: Translate }) {
  const remaining = useCountdown(trial.endsAtSec);
  const expiringSoon = remaining <= 86_400; // ≤ 1 day
  return (
    <output
      data-testid="trial-banner-active"
      className={css({
        p: "3",
        mb: "3",
        rounded: "md",
        borderWidth: "1px",
        borderColor: expiringSoon ? "fg.warning" : "border.default",
        bg: "bg.subtle",
        display: "flex",
        gap: "3",
        flexWrap: "wrap",
        alignItems: "center",
        justifyContent: "space-between",
      })}
    >
      <div>
        <strong>{t("trial.active.title")}</strong>
        <p
          data-testid="trial-countdown"
          className={css({ fontSize: "sm", color: expiringSoon ? "fg.warning" : "fg.muted" })}
        >
          {t("trial.active.remaining", { time: formatRemaining(remaining, t) })}
        </p>
        <p className={css({ fontSize: "xs", color: "fg.muted" })}>
          {t("trial.active.included", {
            flags: trial.grantedFlags.join(", ") || t("trial.active.noFlags"),
          })}
        </p>
      </div>
      <a
        href="./billing"
        data-testid="trial-convert-cta"
        className={css({
          fontWeight: "semibold",
          textDecoration: "underline",
          whiteSpace: "nowrap",
        })}
      >
        {t("trial.active.convert")}
      </a>
    </output>
  );
}

/** A live countdown: seconds remaining to [endsAtSec], re-rendered once per second (never negative). */
function useCountdown(endsAtSec: number): number {
  const [now, setNow] = useState(() => Math.floor(Date.now() / 1000));
  useEffect(() => {
    const id = setInterval(() => setNow(Math.floor(Date.now() / 1000)), 1000);
    return () => clearInterval(id);
  }, []);
  return Math.max(0, endsAtSec - now);
}

function formatRemaining(seconds: number, t: Translate): string {
  if (seconds <= 0) return t("trial.active.expired");
  const days = Math.floor(seconds / 86_400);
  const hours = Math.floor((seconds % 86_400) / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  if (days > 0) return t("trial.active.dhm", { days, hours });
  if (hours > 0) return t("trial.active.hm", { hours, minutes });
  return t("trial.active.ms", { minutes, seconds: seconds % 60 });
}
